import { createApp, watch } from 'vue';
import ListingSearchBar from './components/ListingSearchBar.vue';
import ListingToolbar from './components/ListingToolbar.vue';
import ListingResults from './components/ListingResults.vue';
import ListingDetailPanel from './components/ListingDetailPanel.vue';
import ProjectDetailPanel from './components/ProjectDetailPanel.vue';
import { createSearchApi } from './lib/searchApi';
import { toApiParams } from './lib/apiParams';
import { filterConfig } from './lib/filterConfig';
import { routes } from './lib/routes';
import { useCriteria } from './lib/useCriteria';
import { useMapState } from './lib/useMapState';
import { useResults } from './lib/useResults';
import { useView } from './lib/useView';
import { setTranslations } from './lib/i18n';

const mountEl = document.querySelector('#listing-search');
const toolbarEl = document.querySelector('#listing-toolbar');
const resultsEl = document.querySelector('#listing-results');
const detailEl = document.querySelector('#listing-detail');
const projectDetailEl = document.querySelector('#projects-detail');
const translationsEl = document.querySelector('[data-listing-translations]');

const SEARCH_DEBOUNCE_MS = 300;

if (translationsEl) {
  setTranslations(JSON.parse(translationsEl.dataset.listingTranslations || '{}'));
}

if (mountEl) {
  const api = createSearchApi();
  const bootstrap = filterConfig;
  const { criteria, toQueryString, persist } = useCriteria();
  const { bbox, clearBbox } = useMapState();
  const { view } = useView();

  // The list view shows everything within the filters, so it ignores the map
  // viewport; the stored bbox is kept, not cleared, so switching back to
  // both/map restores the refined viewport and its result set
  const activeBbox = () => (view.value === 'list' ? null : bbox.value);
  const { results, setLoading, setData, setError, setLoadingMore, appendData, stopLoadingMore } =
    useResults();

  // One row: result count, filters, view toggles.
  if (toolbarEl) {
    createApp(ListingToolbar, { housingTypes: bootstrap.housingTypes }).mount(toolbarEl);
  }

  const rangeBounds = {
    m2: [bootstrap.bounds.m2.min, bootstrap.bounds.m2.max],
    perceel: [bootstrap.bounds.perceel.min, bootstrap.bounds.perceel.max]
  };

  // a failed append quietly re-arms instead of replacing loaded cards with
  // the error state
  const loadMore = async () => {
    if (results.loading || results.loadingMore || !results.meta?.hasMore) return;
    const token = setLoadingMore();
    try {
      appendData(token, await api.search(criteria, rangeBounds, results.meta.page + 1, activeBbox()));
    } catch {
      stopLoadingMore();
    }
  };

  if (resultsEl) {
    createApp(ListingResults, { onLoadMore: loadMore }).mount(resultsEl);
  }

  if (detailEl) {
    createApp(ListingDetailPanel).mount(detailEl);
  }

  // Opened from the map's pins for projects without homes yet (WHN-2212)
  if (projectDetailEl) {
    createApp(ProjectDetailPanel).mount(projectDetailEl);
  }

  // Any criteria change (search bar or filter bar) keeps the URL canonical
  // (K4: the query string is the shareable source of truth).
  watch(
    () => toQueryString(rangeBounds),
    (queryString) => {
      window.history.replaceState(null, '', `${window.location.pathname}${queryString ? `?${queryString}` : ''}`);
    }
  );

  let searchTimer = null;
  const runSearch = async () => {
    const token = setLoading();
    try {
      setData(token, await api.search(criteria, rangeBounds, 1, activeBbox()));
    } catch (error) {
      setError(token, error.message || 'Zoeken mislukt');
    }
  };

  // A new location redefines the search area, so the viewport bbox no longer
  // applies; plain filter changes keep it (the camera did not move). Both
  // changes land in the same debounce window below, so this stays one request.
  watch(
    () => [criteria.long, criteria.lat, criteria.radius],
    clearBbox
  );

  // Re-query whenever the API querystring changes; debounced so dragging a
  // slider does not fire a request per step. Immediate to fetch on load.
  watch(
    () => toApiParams(criteria, rangeBounds, 1, activeBbox()).toString(),
    () => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(runSearch, SEARCH_DEBOUNCE_MS);
    },
    { immediate: true }
  );

  const app = createApp(ListingSearchBar, {
    api,
    bootstrap,
    onSearch() {
      persist(rangeBounds);
      const queryString = toQueryString(rangeBounds);
      window.location.href = `${routes.listing}${queryString ? `?${queryString}` : ''}`;
    },
    onPickProject(project) {
      if (project && project.url) window.open(project.url, '_blank', 'noopener');
    }
  });

  app.mount(mountEl);
}
